/**
 * Candidacy Phase Configuration
 * 
 * Single source of truth for candidacy status/phase column definitions.
 * This file defines the visual appearance and properties of each phase column
 * in the candidacies Kanban board.
 */

// Candidacy statuses
export type CandidacyStatus =
  | 'notify'
  | 'invited'
  | 'reminder-sent'
  | 'forwarded-to-broker'
  | 'awaiting-visit'
  | 'awaiting-alternate-selection'
  | 'searchingAppointmentMoment'
  | 'awaiting-interested'
  | 'interested'
  | 'approved'
  | 'rejected'
  | 'not-interested'
  | 'tenant';

export interface CandidacyPhaseColumn {
  id: CandidacyStatus;
  label: string;
  color: string;
  bgColor: string;
  headerBgColor: string;
  borderColor: string;
}

/**
 * Candidacy Phase Columns
 * Defines all available candidacy status/phase columns with their styling
 * 
 * Color scheme reflects action responsibility:
 * - Orange (🟠): We need to act
 * - Blue (🔵): They (candidate) need to act
 * - Green (✅): Positive completion
 * - Red (❌): Negative completion
 */
export const candidacyPhaseColumns: CandidacyPhaseColumn[] = [
  // 🟠 We need to act (Orange)
  {
    id: 'notify',
    label: '1. Uitnodiging versturen',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50/50',
    headerBgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
  },
  // 🔵 They need to act (Blue)
  {
    id: 'invited',
    label: '2. Uitnodiging verstuurd',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50/50',
    headerBgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
  },
  {
    id: 'reminder-sent',
    label: '2b. Herinneringsmail verstuurd',
    color: 'text-violet-700',
    bgColor: 'bg-violet-50/50',
    headerBgColor: 'bg-violet-50',
    borderColor: 'border-violet-300',
  },
  {
    id: 'forwarded-to-broker',
    label: '2c. Doorgestuurd naar makelaar',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50/50',
    headerBgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
  },
  {
    id: 'awaiting-visit',
    label: '3. Bezoek ingepland',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50/50',
    headerBgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
  },
  {
    id: 'awaiting-alternate-selection',
    label: '2b. Alternatieve datum selecteren',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50/50',
    headerBgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
  },
  // 🔴 Urgent action needed (Red)
  {
    id: 'searchingAppointmentMoment',
    label: '2a. Afspraak moment zoeken',
    color: 'text-red-700',
    bgColor: 'bg-red-50/50',
    headerBgColor: 'bg-red-50',
    borderColor: 'border-red-300',
  },
  {
    id: 'awaiting-interested',
    label: '4a. Interesse?',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50/50',
    headerBgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
  },
  {
    id: 'interested',
    label: '4b. Wil huren',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50/50',
    headerBgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
  },
  // ✅ Positive completion (Green)
  {
    id: 'approved',
    label: '5a. Goedgekeurd (zelfde als huurder)',
    color: 'text-green-700',
    bgColor: 'bg-green-50/50',
    headerBgColor: 'bg-green-50',
    borderColor: 'border-green-300',
  },
  {
    id: 'tenant',
    label: 'Huurder (zelfde als goedgekeurd)',
    color: 'text-green-700',
    bgColor: 'bg-green-50/50',
    headerBgColor: 'bg-green-50',
    borderColor: 'border-green-300',
  },
  // ❌ Negative completion (Red)
  {
    id: 'rejected',
    label: '5b. Afgewezen (Huurder voldoet niet aan criteria of pand bezet)',
    color: 'text-red-700',
    bgColor: 'bg-red-50/50',
    headerBgColor: 'bg-red-50',
    borderColor: 'border-red-300',
  },
  {
    id: 'not-interested',
    label: 'Geen interesse',
    color: 'text-red-700',
    bgColor: 'bg-red-50/50',
    headerBgColor: 'bg-red-50',
    borderColor: 'border-red-300',
  },
];

/**
 * Helper function to get status label from phase columns
 * Returns the label with workflow numbers (e.g., "1. Uitnodiging versturen")
 */
export const getStatusLabel = (status: CandidacyStatus): string => {
  const column = candidacyPhaseColumns.find(col => col.id === status);
  return column?.label || status;
};

